import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
  SelectChangeEvent,
  Alert,
} from '@mui/material';
import {
  createTeacherDirect,
  updateTeacherDirect,
  assignSubjectsToTeacherDirect,
  getSubjectsDirect,
  getTeacherTypesDirect,
  sendInviteDirect,
} from '@timetable/api';
import type { TeacherWithRelations, CreateTeacherRequest, UpdateTeacherRequest } from '@timetable/types';
import { useToast } from '../Toast';
import { SubjectAssignment } from './SubjectAssignment';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../lib/queryKeys';

interface TeacherFormDialogProps {
  open: boolean;
  teacher: TeacherWithRelations | null;
  onClose: () => void;
  /** School name used in the invite email */
  schoolName?: string;
}

interface FormData {
  name: string;
  email: string;
  phone_number: string;
  teacher_type_id: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone_number?: string;
  teacher_type_id?: string;
}

export function TeacherFormDialog({ open, teacher, onClose, schoolName }: TeacherFormDialogProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const [inviteAlert, setInviteAlert] = useState<'sent' | 'email_exists' | 'failed' | null>(null);

  // Read existing teachers from cache for duplicate email check
  const { data: existingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '', includeInactive: true }),
    queryFn: () => import('@timetable/api').then(m => m.getTeachersDirect({ school_id: schoolContext || '', includeInactive: true })),
    enabled: !!schoolContext && open,
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone_number: '',
    teacher_type_id: '',
  });

  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch teacher types
  const { data: teacherTypes = [] } = useQuery({
    queryKey: ['teacherTypes'],
    queryFn: getTeacherTypesDirect,
  });

  // Fetch subjects for assignment
  const { data: subjects, isLoading: subjectsLoading, error: subjectsError } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () => {
      console.log('Fetching subjects for schoolContext:', schoolContext);
      return getSubjectsDirect({ school_id: schoolContext || undefined });
    },
    enabled: !!schoolContext && open,
  });

  // Log subjects data when it changes
  useEffect(() => {
    if (open && subjects) {
      console.log('Subjects loaded:', subjects.length, subjects);
    }
    if (open && subjectsError) {
      console.error('Subjects error:', subjectsError);
    }
  }, [open, subjects, subjectsError]);

  useEffect(() => {
    if (teacher) {
      setFormData({
        name: teacher.name,
        email: teacher.email || '',
        phone_number: teacher.phone_number || '',
        teacher_type_id: teacher.teacher_type_id,
      });
      setSelectedSubjects(teacher.subjects?.map(s => s.id) || []);
    } else {
      setFormData({
        name: '',
        email: '',
        phone_number: '',
        teacher_type_id: '',
      });
      setSelectedSubjects([]);
    }
    setErrors({});
  }, [teacher, open]);

  const createMutation = useMutation({
    mutationFn: createTeacherDirect,
    onSuccess: async (newTeacher) => {
      // Assign subjects if any selected
      if (selectedSubjects.length > 0) {
        await assignSubjectsToTeacherDirect({
          teacher_id: newTeacher.id,
          subject_ids: selectedSubjects,
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.teachers.all });

      // Send invite if email provided
      if (formData.email && schoolContext) {
        try {
          const result = await sendInviteDirect({
            email: formData.email,
            role: 'teacher',
            schoolId: schoolContext,
            schoolName: schoolName ?? schoolContext,
            name: formData.name,
            refId: newTeacher.id,
          });
          setInviteAlert(result.emailExists ? 'email_exists' : 'sent');
        } catch {
          setInviteAlert('failed');
        }
        // Keep dialog open briefly to show alert, then close
        setTimeout(() => { setInviteAlert(null); onClose(); }, 3000);
        showToast('Teacher created successfully', 'success');
      } else {
        showToast('Teacher created successfully', 'success');
        onClose();
      }
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create teacher', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeacherRequest }) =>
      updateTeacherDirect(id, data),
    onSuccess: async () => {
      // Update subject assignments
      if (teacher) {
        await assignSubjectsToTeacherDirect({
          teacher_id: teacher.id,
          subject_ids: selectedSubjects,
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.teachers.all });
      showToast('Teacher updated successfully', 'success');
      onClose();
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update teacher', 'error');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else {
      // Check for duplicate email among existing teachers (exclude self when editing)
      const duplicate = (existingTeachers ?? []).find(
        (t) =>
          t.email?.toLowerCase() === formData.email.toLowerCase() &&
          t.id !== teacher?.id
      );
      if (duplicate) {
        newErrors.email = `This email is already used by ${duplicate.name}`;
      }
    }

    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number must be exactly 10 digits';
    }

    if (!formData.teacher_type_id) {
      newErrors.teacher_type_id = 'Teacher type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (teacher) {
      // Update existing teacher
      updateMutation.mutate({
        id: teacher.id,
        data: {
          name: formData.name,
          email: formData.email || undefined,
          phone_number: formData.phone_number || undefined,
          teacher_type_id: formData.teacher_type_id,
        },
      });
    } else {
      // Create new teacher
      const createData: CreateTeacherRequest = {
        school_id: schoolContext || '',
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number || undefined,
        teacher_type_id: formData.teacher_type_id,
      };
      createMutation.mutate(createData);
    }
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'phone_number'
      ? event.target.value.replace(/\D/g, '').slice(0, 10)
      : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormData) => (
    event: SelectChangeEvent<string>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{teacher ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {inviteAlert === 'sent' && (
            <Alert severity="success">Activation email sent to {formData.email}</Alert>
          )}
          {inviteAlert === 'email_exists' && (
            <Alert severity="warning">
              <strong>{formData.email}</strong> already has an account. They can log in directly.
            </Alert>
          )}
          {inviteAlert === 'failed' && (
            <Alert severity="warning">Teacher created, but the activation email could not be sent.</Alert>
          )}
          <TextField
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            required
            fullWidth
          />

          <TextField
            label="Phone Number"
            value={formData.phone_number}
            onChange={handleChange('phone_number')}
            error={!!errors.phone_number}
            helperText={errors.phone_number || '10-digit number'}
            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
            fullWidth
          />

          <FormControl fullWidth error={!!errors.teacher_type_id} required>
            <InputLabel>Teacher Type</InputLabel>
            <Select
              value={formData.teacher_type_id}
              onChange={handleSelectChange('teacher_type_id')}
              label="Teacher Type"
            >
              {teacherTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.type_name}
                </MenuItem>
              ))}
            </Select>
            {errors.teacher_type_id && (
              <FormHelperText>{errors.teacher_type_id}</FormHelperText>
            )}
          </FormControl>

          <SubjectAssignment
            subjects={subjects || []}
            selectedSubjects={selectedSubjects}
            onSelectionChange={setSelectedSubjects}
            isLoading={subjectsLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending || updateMutation.isPending}
        >
          {teacher ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
